import type { components } from "@/api/contract";
import type { SseEvent } from "@/api/sse";

export type ToolProposal = components["schemas"]["ToolProposal"];
export type ToolResult = components["schemas"]["ToolResult"];
export type ToolResultPayload = ToolResult["result"];
/** Structured MCP connector preview riding `tool_succeeded` (read-only tools). */
export type McpToolPreview = NonNullable<ToolResult["preview"]>;
export type Source = components["schemas"]["Source"];

/**
 * The chat run SSE protocol as a discriminated union (backend `internal/handler`
 * stream.go / runs_tools.go). `parseChatEvent` turns a raw `SseEvent` (event name
 * + JSON string) into one of these — the controller switches on `type`. Pure +
 * defensive: malformed JSON or unknown events return `null` rather than throw.
 */
export type ChatStreamEvent =
  | {
      type: "run";
      runId: string;
      conversationId: string;
      inputMessageId?: string;
      activeLeafMessageId?: string;
    }
  | { type: "status"; phase: "searching" | "thinking" }
  | { type: "sources"; sources: Source[] }
  | { type: "delta"; content: string }
  | { type: "error"; message: string; code?: string }
  | {
      type: "done";
      status: "completed" | "cancelled";
      runId?: string;
      conversationId?: string;
      outputMessageId?: string;
      activeLeafMessageId?: string;
    }
  | { type: "tool_proposal"; proposal: ToolProposal }
  | { type: "tool_executing"; toolCallId: string }
  | {
      type: "tool_succeeded";
      toolCallId: string;
      result: ToolResultPayload;
      resultPreview?: string;
      preview?: McpToolPreview | null;
    }
  | { type: "tool_failed"; toolCallId: string; error?: string }
  | { type: "tool_declined"; toolCallId: string }
  | { type: "tool_rejected"; toolCallId: string; reason?: string; error?: string }
  | { type: "tool_expired"; toolCallId: string };

type Json = Record<string, unknown>;

function str(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/** Parse one raw SSE event into a typed chat event, or null to ignore it. */
export function parseChatEvent(raw: SseEvent): ChatStreamEvent | null {
  let data: Json;
  try {
    data = JSON.parse(raw.data) as Json;
  } catch {
    return null;
  }

  switch (raw.event) {
    case "run":
      return {
        type: "run",
        runId: String(data.run_id ?? ""),
        conversationId: String(data.conversation_id ?? ""),
        inputMessageId: str(data.input_message_id),
        activeLeafMessageId: str(data.active_leaf_message_id),
      };
    case "status": {
      const phase = str(data.type);
      if (phase === "searching" || phase === "thinking") return { type: "status", phase };
      return null;
    }
    case "sources":
      return { type: "sources", sources: Array.isArray(data) ? (data as Source[]) : [] };
    case "delta":
      return { type: "delta", content: String(data.content ?? "") };
    case "usage":
      // Token accounting — not surfaced in the UI; ignore.
      return null;
    case "error":
      return { type: "error", message: String(data.message ?? "error"), code: str(data.code) };
    case "done":
      return {
        type: "done",
        status: data.status === "cancelled" ? "cancelled" : "completed",
        runId: str(data.run_id),
        conversationId: str(data.conversation_id),
        outputMessageId: str(data.output_message_id),
        activeLeafMessageId: str(data.active_leaf_message_id),
      };
    case "tool_proposal":
      return { type: "tool_proposal", proposal: data as unknown as ToolProposal };
    case "tool_executing":
      return { type: "tool_executing", toolCallId: String(data.tool_call_id ?? "") };
    case "tool_succeeded":
      return {
        type: "tool_succeeded",
        toolCallId: String(data.tool_call_id ?? ""),
        result: (data.result ?? null) as ToolResultPayload,
        resultPreview: str(data.result_preview),
        preview: (data.preview ?? null) as McpToolPreview | null,
      };
    case "tool_failed":
      return { type: "tool_failed", toolCallId: String(data.tool_call_id ?? ""), error: str(data.error) };
    case "tool_declined":
      return { type: "tool_declined", toolCallId: String(data.tool_call_id ?? "") };
    case "tool_rejected":
      return {
        type: "tool_rejected",
        toolCallId: String(data.tool_call_id ?? ""),
        reason: str(data.reason),
        error: str(data.error),
      };
    case "tool_expired":
      return { type: "tool_expired", toolCallId: String(data.tool_call_id ?? "") };
    default:
      return null;
  }
}
